import { Component, EventEmitter, Input, OnInit, ViewChild } from '@angular/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { FreightPayer } from '../../../../../../../shared/freight-payers/models/FreightPayer';
import { NgbDateParserFormatter, NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import dayjs from 'dayjs';
import { bootstrapCalendarFill } from '@ng-icons/bootstrap-icons';
import { BryntumDurationFieldComponent, BryntumDurationFieldProps, BryntumGridModule, BryntumNumberFieldComponent } from '@bryntum/grid-angular';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CustomDateFormatter } from '../../../../../../../shared/ngb-datepicker/NgbDateCustomParserFormatter';
import { Job } from '../../../../../../../shared/Jobbing/models/Job';
import { DateHelper } from '@bryntum/grid';

@Component({
  selector: 'app-job-information-form',
  standalone: true,
  imports: [NgSelectModule, NgbDatepickerModule, NgIconComponent, BryntumGridModule, ReactiveFormsModule],
  templateUrl: './job-information-form.component.html',
  styleUrl: './job-information-form.component.scss',
  providers: [
    {
      provide: NgbDateParserFormatter,
      useClass: CustomDateFormatter
    }
  ],
  viewProviders: [
    provideIcons({
      bootstrapCalendarFill
    })
  ]
})
export class JobInformationFormComponent implements OnInit {

  @Input()
  freightPayers: FreightPayer[] | null = null;

  @Input()
  formGroup!: FormGroup;

  lockdown: boolean = false;

  @Input()
  servicePromiseOptions!: any[] | null;

  @Input()
  resetEmitter!: EventEmitter<boolean>;

  @Input()
  priorityOptions!: any[] | null;

  lspSpecificDateRequired: boolean = false;
  dspSpecificDateRequired: boolean = false;

  @ViewChild("estimatedHoursInput") estimatedHoursInput!: BryntumDurationFieldComponent;

  constructor(private fb: FormBuilder) { }

  ngOnInit(): void {
    this.resetEmitter.subscribe(() => {
      this.formGroup.get('jobInformation')?.patchValue({
        estimatedHours: 3600000,
        dspTime: dayjs().toDate(),
        lspTime: dayjs().toDate(),
        timeBased: false,
        orderNumber: "",
      });
    });

    this.initializeForm();
  }

  initializeForm() {
    this.formGroup.addControl('jobInformation', this.fb.group({
      freightPayer: this.fb.control(null, [Validators.required]),
      requiredCompletionDate: this.fb.control({
        year: dayjs().year(),
        // month is 0 based
        month: dayjs().month() + 1,
        day: dayjs().date()
      }, [Validators.required]),

      deliveryServicePromise: this.fb.control(1, [Validators.required]),
      dspTime: this.fb.control(dayjs().toDate()),
      loadingServicePromise: this.fb.control(1, [Validators.required]),
      lspTime: this.fb.control(dayjs().toDate()),
      orderNumber: this.fb.control('', [Validators.required]),
      timeBased: this.fb.control(false),
      estimatedHours: this.fb.control(3600000, [Validators.required]),
      jobReference: this.fb.control(''),
      priority: this.fb.control(2, [Validators.required]),
      jobDescription: this.fb.control(''),
    }));

    this.formGroup.get('jobInformation')?.get('deliveryServicePromise')?.valueChanges.subscribe((value) => {
      this.dspSpecificDateRequired = value === 4;
      this.formGroup.get('jobInformation')?.get('dspTime')?.setValidators(this.dspSpecificDateRequired ? [Validators.required] : null);
      this.formGroup.updateValueAndValidity();
    });

    this.formGroup.get('jobInformation')?.get('loadingServicePromise')?.valueChanges.subscribe((value) => {
      this.lspSpecificDateRequired = value === 4;
      this.formGroup.get('jobInformation')?.get('lspTime')?.setValidators(this.dspSpecificDateRequired ? [Validators.required] : null);
      this.formGroup.updateValueAndValidity();
    });

    this.formGroup.get('jobInformation')?.get('estimatedHours')?.valueChanges.subscribe((value) => {
      // value is in milliseconds so needs to be converted
      if (value === null || value === undefined) {
        this.estimatedHoursInput.instance.value = DateHelper.as('hours', 1, 'hours');
      } else {
        this.estimatedHoursInput.instance.value = DateHelper.as('hours', value, 'milliseconds');
      }
    });
  }

  estimatedHoursChange($event: any) {
    this.getFormGroup().patchValue({
      estimatedHours: $event.value.milliseconds
    }, { emitEvent: false });
    this.getFormGroup().markAsDirty();
    this.getFormGroup().markAsTouched();
  }

  getFormGroup() {
    if (this.formGroup?.get('jobInformation') === null) {
      this.initializeForm();
    }
    return this.formGroup.get('jobInformation') as FormGroup;
  }

  getDspTime() {
    return this.getFormGroup().get('dspTime')?.value;
  }

  onDspTimeChange($event: any) {
    const value = dayjs($event.value).toDate();
    this.getFormGroup().patchValue({
      dspTime: value
    });

  }

  getLspTime() {
    return this.getFormGroup().get('lspTime')?.value;
  }

  onLspTimeChange($event: any) {
    const value = dayjs($event.value).toDate();
    this.getFormGroup().patchValue({
      lspTime: value
    });
  }

}
